import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { Pagination, PermissionSetContainerModel, PermissionTemplateContainerModel, PermissionTemplateService } from 'blg-akaun-ts-lib';
import { ToastrService } from 'ngx-toastr';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../visa';
import { zip } from 'rxjs';
import { concatMap, switchMap, toArray } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { UserPermActions } from '../../../../../../../modules/permission/user-permissions-controller/actions';
import { UserPermSelectors } from '../../../../../../../modules/permission/user-permissions-controller/selectors';
import { PermissionSetModel, PermSetLine } from '../../../../../../../models/user-permissions.model';
import { PermissionStates } from '../../../../../permission-controller/root';
import { SessionStates } from '../../../../../../../modules/session/session-controller/states';
import { SessionSelectors } from '../../../../../../../modules/session/session-controller/selectors';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-user-permission-set-add',
  templateUrl: './user-permission-set-add.component.html',
  styleUrls: ['./user-permission-set-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class UserPermissionSetAddComponent extends ViewColumnComponent implements OnInit, OnDestroy {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 9;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  prevIndex: number;
  private prevLocalState: any;

  apiVisa = AppConfig.apiVisa;
  masterVisa = AppConfig.masterVisa;

  public form: FormGroup;

  // keep track of form being enabled/disabled
  formDisabled: boolean = false;

  // no leakage of subscribe calls
  private subSink = new SubSink;

  // fields that require options
  status: string;
  statusArr: any = [];
  newStatusArr: any = [];

  // perm templates
  permTemplates: PermissionTemplateContainerModel[] = [];
  permTemplateArr = [];
  newPermTemplateArr = [];

  appletGuid: string = sessionStorage.getItem('appletGuid');
  // tenantCode: string = sessionStorage.getItem('tenantCode');
  tenantGuid: string;

  newPermSet: PermissionSetModel;

  //translation variable
  title: string;
  back_button: string;
  save_button: string;
  perm_set_name_text: string;
  please_insert_name_text: string;
  perm_set_code_text: string;
  code_must_be_unique_text: string;
  please_insert_code_text: string;
  code_should_be_less_than_255_characters_text: string;
  perm_template_text: string;
  not_a_valid_entry_text: string;
  please_choose_a_permission_template_text: string;
  configure_permission_targets_text: string;
  please_configure_permission_targets_text: string;
  description_text: string;
  status_text: string;
  active_text: string;
  inactive_text: string

  constructor(
    private permTemplateService: PermissionTemplateService,
    private toasterService: ToastrService,
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private readonly sessionStore: Store<SessionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.add_permission_set;
        this.back_button = res.back_button;
        this.save_button = res.save_button;
        this.perm_set_name_text = res.perm_set_name;
        this.please_insert_name_text = res.please_insert_name;
        this.perm_set_code_text = res.perm_set_code;
        this.code_must_be_unique_text = res.code_must_be_unique;
        this.please_insert_code_text = res.please_insert_code;
        this.code_should_be_less_than_255_characters_text = res.code_should_be_less_than_255_characters;
        this.perm_template_text = res.perm_template;
        this.not_a_valid_entry_text = res.invalid_entry;
        this.please_choose_a_permission_template_text = res.please_choose_a_permission_template;
        this.configure_permission_targets_text = res.configure_permission_targets;
        this.please_configure_permission_targets_text = res.please_configure_permission_targets;
        this.description_text = res.description;
        this.status_text = res.status;
        this.active_text = res.active;
        this.inactive_text = res.inactive;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Add Permission Set';
      this.back_button = 'Back';
      this.save_button = 'SAVE';
      this.perm_set_name_text = 'Perm Set Name';
      this.please_insert_name_text = 'Please insert name';
      this.perm_set_code_text = 'Perm Set Code';
      this.code_must_be_unique_text = 'Code must be unique';
      this.please_insert_code_text = 'Please insert code';
      this.code_should_be_less_than_255_characters_text = 'Code should be less than 255 characters';
      this.perm_template_text = 'Perm Template';
      this.not_a_valid_entry_text = 'Not a valid entry';
      this.please_choose_a_permission_template_text = 'Please choose a permission template';
      this.configure_permission_targets_text = 'Configure Permission Targets';
      this.please_configure_permission_targets_text = 'Please configure permission targets!';
      this.description_text = 'Description';
      this.status_text = 'Status';
      this.active_text = 'ACTIVE';
      this.inactive_text = 'INACTIVE';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.statusArr = [
      { value: 'ACTIVE', viewValue: this.active_text },  // Use the translated value
      { value: 'INACTIVE', viewValue: this.inactive_text }  // Use the translated value
    ];
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      code: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      description: [''],
      currStatus: [''],
      status: ['', Validators.compose([Validators.required])],
      currPermTemplate: [''],
      permTemplate: ['', Validators.compose([Validators.required])],
      configureTargets: [''],

      // uneditable
      appletGuid: [this.appletGuid]
    })

    this.newStatusArr = this.statusArr;

    const AppletLoginSubjectLink$ = this.sessionStore.select(SessionSelectors.selectAppletLoginSubjectLink);

    this.subSink.sink = AppletLoginSubjectLink$.subscribe(session => {
      if(session) {
        console.log("Session tenant guid: ",session);
        this.tenantGuid = session.tenant_guid.toString();
      }
    })

    const permTemplate$ = this.getPermTemplates();
    const newPermSet$ = this.store.select(UserPermSelectors.selectNewPermSet);

    // display perm templates
    this.subSink.sink = zip(permTemplate$, newPermSet$)
      .subscribe(([_, permSet]) => {
        if (permSet) {
          this.newPermSet = permSet;

          // find the perm template that was previously chosen
          const permTemplate = this.permTemplateArr.filter(x => x.guid.toString() == permSet.app_perm_set_hdr.parent_guid.toString())[0];
          // check if any default targets have not been added to the perm set line if any
          const targetsConfigured = permSet.permSetLines.filter(x => x.app_perm_set_line.target_hdr_table != null && x.default_target_hdr_name == null).length == 0;
          this.form.patchValue({
            name: permSet.app_perm_set_hdr.name,
            code: permSet.app_perm_set_hdr.code,
            description: permSet.app_perm_set_hdr.description,
            status: permSet.app_perm_set_hdr.status,
            permTemplate: permTemplate,
            configureTargets: targetsConfigured ? 'CONFIGURED' : ""
          })
        }
      })
  }

  /**
   * Get all permission templates.
   */
   getPermTemplates() {
    return this.permTemplateService.getByLogin(this.appletGuid, this.tenantGuid, this.masterVisa)
      .pipe(
        switchMap((x) => x.data),
        toArray(),
        concatMap(x => {
          this.permTemplates = x;
          for (const key in this.permTemplates) {
            this.permTemplateArr.push({
              guid: this.permTemplates[key].app_perm_template_hdr.guid,
              name: this.permTemplates[key].app_perm_template_hdr.name,
              lines: this.permTemplates[key].app_perm_template_lines
            });
          }
          this.newPermTemplateArr = this.permTemplateArr;

          return x;
        })
      )
  }

  /**
   * This function is called whe the user clicks on the save button.
   * This will dispatch an NgRx action to create a new permission set.
   *
   */
   onSubmit() {
    // check if the perm template chosen is the same as the perm template configured
    if (this.form.value.permTemplate.guid.toString() != this.newPermSet.app_perm_set_hdr.parent_guid.toString()) {
      this.toasterService.error(
        'Please configure targets for the permission template chosen!',
        'Error',
        {
          tapToDismiss: true,
          progressBar: true,
          timeOut: 1300
        }
      );
    } else {
      this.newPermSet.app_perm_set_hdr.name = this.form.value.name;
      this.newPermSet.app_perm_set_hdr.code = this.form.value.code;
      this.newPermSet.app_perm_set_hdr.description = this.form.value.description;
      this.newPermSet.app_perm_set_hdr.status = this.form.value.status;

      const permSet = new PermissionSetContainerModel();
      permSet.app_perm_set_hdr = this.newPermSet.app_perm_set_hdr;
      permSet.app_perm_set_lines = this.newPermSet.permSetLines.map(x => x.app_perm_set_line);

      this.store.dispatch(UserPermActions.createPermSetInit({ assigneeHdrTable: "app_login_subject", appletGuid: this.appletGuid, permSet: permSet }));
      // this.store.dispatch(UserPermActions.addPermSetInit({ assigneeHdrTable: "app_login_subject", appletGuid: this.appletGuid, permSet: permSet}));
      this.store.dispatch(UserPermActions.setNewPermSet({permSet: null}));
      this.onReturn();
    }

  }

 /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
  applyStatusFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newStatusArr = this.statusArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * Dynamically change the options of permission templates shown on the screen by
   * applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
   applyPermTemplateFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newPermTemplateArr = this.permTemplateArr.filter( (option) =>
      option.name.toLowerCase().includes(filterValue) );
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateAdd: false,
      deactivateList: false,
      deactivateReturn: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
    this.store.dispatch(UserPermActions.setNewPermSet({permSet: null}));
  }

  /**
   * Configure targets in perm set lines.
   *
   */
  selectTemplate() {
    // check if the user has already selected a permission template
    const permTemplate = this.form.value.permTemplate;

    if (!permTemplate) {
      this.toasterService.error(
        'Please choose a permission template before configuring targets!',
        'Error',
        {
          tapToDismiss: true,
          progressBar: true,
          timeOut: 1300
        }
      );
    } else {
      const newPermSet = new PermissionSetModel();
      newPermSet.app_perm_set_hdr.name = this.form.value.name;
      newPermSet.app_perm_set_hdr.code = this.form.value.code;
      newPermSet.app_perm_set_hdr.description = this.form.value.description;
      newPermSet.app_perm_set_hdr.status = this.form.value.status;
      // set parent_guid to this perm template guid
      newPermSet.app_perm_set_hdr.parent_guid = permTemplate.guid;
      // set applet guid
      newPermSet.app_perm_set_hdr.applet_guid = this.appletGuid;

      // copy all perm template lines to perm set lines

      if (this.newPermSet && this.newPermSet.app_perm_set_hdr.parent_guid.toString() == permTemplate.guid.toString()) {
        newPermSet.permSetLines = this.newPermSet.permSetLines;
      } else {
        permTemplate.lines.forEach(line => {
          const permSetLine = new PermSetLine();
          permSetLine.app_perm_set_line.parent_guid = line.guid;
          permSetLine.app_perm_set_line.target_hdr_table = line.target_options_json.default_target_hdr_table != "" ? line.target_options_json.default_target_hdr_table : null;
          permSetLine.app_perm_set_line.target_options_json = line.target_options_json;
          permSetLine.app_perm_set_line.type = line.type;
          permSetLine.app_perm_set_line.code = line.code;
          permSetLine.app_perm_set_line.name = line.name;
          permSetLine.app_perm_set_line.target_type = line.target_type;

          // find the default target view name
          const allTargets = line.target_options_json.target_hdr_table_view_name.split(",").map(x => x.trim());
          const allTargetTables = line.target_options_json.target_hdr_table.split(",").map(x => x.trim());
          const index = allTargetTables.indexOf(line.target_options_json.default_target_hdr_table);
          permSetLine.default_target_view_name = allTargets[index];

          newPermSet.permSetLines.push(permSetLine);
        })
      }

      // save this permission set in the store
      this.store.dispatch(UserPermActions.setNewPermSet({permSet: newPermSet}));
      // save selected perm template in the store
      this.store.dispatch(UserPermActions.setPermTemplate({permTemplate}));

      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateReturn: true
        });
      this.viewColFacade.onNext(4);
    }

  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
   ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
